import React from 'react';
import PropTypes from 'prop-types';

export class ErrorBoundary extends React.Component {
  state = {
    hasError: false,
  }

  static propTypes = {
    children: PropTypes.node.isRequired,

    /** Elements to render when an error is thrown. */
    fallbackUI: PropTypes.node,

    /** Event that fires when an error is thrown. */
    onError: PropTypes.func,

    /**
     * Unhandled promise rejections (e.g. an API failure, or throwing an error in an async function) won't be caught by the error boundary.
     * Use this prop to set a global window.addEventListener('unhandledrejection') which catches the unhandled rejections
     * and emits an error that can be caught by the error boundary.
     *
     * IMPORTANT NOTE: This is a risky prop to use on any page with multiple React apps, a mixture of React and non-React code,
     * or a page that loads third-party code. Unhandled promise rejections on any part of the page will cause your ErrorBoundary
     * to display an error fallback. See https://ziprecruiter.slack.com/archives/C06VCPNBE/p1676576824190229
     */
    catchUnhandledPromiseRejection: PropTypes.bool,
  }

  static defaultProps = {
    fallbackUI: null,
    onError: null,
    catchUnhandledPromiseRejection: false,
  }

  static getDerivedStateFromError(/* error */) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    const { onError } = this.props;

    if (onError) {
      onError(error, errorInfo);
    }
  }

  componentWillUnmount() {
    const { catchUnhandledPromiseRejection } = this.props;
    if (catchUnhandledPromiseRejection) {
      window.removeEventListener('unhandledrejection', this.unhandledRejection);
    }
  }

  unhandledRejection(event) {
    const { onError } = this.props;
    // try to extract error, otherwise fall back to default
    let error = new Error('unhandled promise rejection');
    if (event && event.reason instanceof Error) {
      error = event.reason;
    }
    if (onError) {
      onError(error, { componentStack: error.stack });
    }
    this.setState({ hasError: true });
  }

  render() {
    const { children, fallbackUI, catchUnhandledPromiseRejection } = this.props;
    const { hasError } = this.state;

    if (catchUnhandledPromiseRejection) {
      window.addEventListener('unhandledrejection', this.unhandledRejection.bind(this));
    }

    if (fallbackUI && hasError) {
      // You can render any custom fallback UI
      return fallbackUI;
    }

    return children;
  }
}

export default ErrorBoundary;
